import type {
    IdeaSelectionModelEventBus,
    IdeaSelectionResolvers,
    IdeaSelectionRuntimeState,
} from './IdeaSelectionModel';
import { IdeaSelectionModel, ideaSelectionStateSchema } from './IdeaSelectionModel';
import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type { ColumnOptions } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate, TemplateColumn } from '@thinkalpha/ui-types/contracts/column-template';
import type { StrategyWidgetModel } from '@thinkalpha/ui-types/contracts/workspace';
import { t } from 'i18next';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import type { ClientSortModel } from 'src/features/table/types';
import { ColumnOptionsModel, columnOptionsStateSchema } from 'src/models/ColumnOptionsModel';
import type { ColumnOptionsModelEventBus } from 'src/models/ColumnOptionsModel';
import { ColumnTemplateModel, columnTemplateStateSchema } from 'src/models/ColumnTemplateModel';
import type { ColumnTemplateModelEventBus, ColumnTemplateRef } from 'src/models/ColumnTemplateModel';
import { StickyFilterModel, stickyFilterStateSchema } from 'src/models/StickyFilterModel';
import type { StickyFilterModelEventBus } from 'src/models/StickyFilterModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import { ContainerChannelSlice } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import type { ContainerChannelSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import { ContainerWidgetTabSlice } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { ContainerWidgetTabSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ByIdState } from 'src/store/reducers/tab/byId';
import type { ContainerViewModel, WidgetTabViewModel } from 'src/store/types';
import type { ReactBindings } from 'src/types/bindings';
import type {
    ClientSortModelSignal,
    InstrumentChangeEvent,
    IsScrollingEnabledSignal,
    NameSignal,
    SetClientSortModelEvent,
    ToggleScrollingEvent,
} from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

/**
 * Persisted widget state — the `StrategyWidgetModel` content fields. Composed
 * from the sub-model state schemas so each concern owns its slice of the
 * blob.
 */
export const resultsWidgetStateSchema = z.object({
    ...ideaSelectionStateSchema.shape,
    ...columnTemplateStateSchema.shape,
    ...stickyFilterStateSchema.shape,
    ...columnOptionsStateSchema.shape,
    scrolling: z.boolean().default(false),
});

export type ResultsWidgetState = z.output<typeof resultsWidgetStateSchema>;

/**
 * Compile-time guard: the persisted state must stay spread-compatible with
 * the wire `StrategyWidgetModel` content, because `Container.toJSON` merges
 * it over the `{type, channelId}` base to assemble the workspace blob.
 */
type StrategyWidgetContent = Omit<StrategyWidgetModel, 'type' | 'channelId' | 'keyboardCommands'>;
const _assertStateIsWireCompatible = (state: ResultsWidgetState): StrategyWidgetContent => state;
void _assertStateIsWireCompatible;

export type ResultsWidgetLoadStage = 'column-template' | 'fast-ideas' | 'slow-idea' | 'table';

/**
 * Runtime lifecycle of the Results widget.
 *
 * - `loading`: resolving the column template and idea DTOs. The table query
 *   is gated off in this state, so the first table request is only issued
 *   once every input is final — a single, stable table creation.
 * - `ready`: all inputs resolved; the table query is live and reacts to
 *   incremental changes (sort, filter, idea selection, template edits).
 * - `error`: a required input (or the table run itself) failed. `stage`
 *   identifies which.
 */
export type ResultsWidgetRuntimeState =
    | { type: 'loading' }
    | { type: 'ready' }
    | { type: 'error'; stage: ResultsWidgetLoadStage; error: Error };

export type ResultsSelfOwnedEventBus = EventBus<
    EventBusSignals<ContainerChannelSliceEventBus> &
        EventBusSignals<ContainerWidgetTabSliceEventBus> &
        EventBusSignals<StickyFilterModelEventBus> &
        EventBusSignals<ColumnOptionsModelEventBus> &
        EventBusSignals<ColumnTemplateModelEventBus> &
        EventBusSignals<IdeaSelectionModelEventBus> &
        NameSignal &
        IsScrollingEnabledSignal &
        ClientSortModelSignal,
    EventBusEvents<ContainerChannelSliceEventBus> &
        EventBusEvents<ContainerWidgetTabSliceEventBus> &
        EventBusEvents<StickyFilterModelEventBus> &
        EventBusEvents<ColumnOptionsModelEventBus> &
        EventBusEvents<ColumnTemplateModelEventBus> &
        EventBusEvents<IdeaSelectionModelEventBus> &
        ToggleScrollingEvent &
        SetClientSortModelEvent &
        InstrumentChangeEvent
>;

type ResultsSelfOwnedModelInitBase = {
    persisted: ResultsWidgetState;
    logger: Logger;
    queryClient: ReactBindings['QueryClient'];
    ideaResolvers: IdeaSelectionResolvers;
    /** Gates multi-fast-idea selection; when off, only the first idea reaches the table. */
    multiIdeaSelectEnabled: ReadonlySignal<boolean>;
};

type ResultsSelfOwnedWorkspaceInit = ResultsSelfOwnedModelInitBase & {
    mode: 'workspace';
    container: WidgetHostContainer;
    tabId: WidgetId;
    workspaceService: Pick<WorkspaceService, 'publishLinkIntent'>;
};

type ResultsSelfOwnedStandaloneInit = ResultsSelfOwnedModelInitBase & {
    mode: 'standalone';
};

export type ResultsSelfOwnedModelInit = ResultsSelfOwnedWorkspaceInit | ResultsSelfOwnedStandaloneInit;

export interface ResultsSelfOwnedModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: ResultsSelfOwnedEventBus;
    /** loading/error status of input resolution (template + ideas); table status is layered on by ResultsTableModel. */
    readonly inputState: ReadonlySignal<ResultsWidgetRuntimeState>;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    readonly columnTemplateRef: ReadonlySignal<ColumnTemplateRef>;
    readonly fastIdeas: ReadonlySignal<IdeaDTO<IdeaPlanDTO>[]>;
    readonly slowIdea: ReadonlySignal<IdeaDTO<IdeaPlanDTO> | null>;
    readonly fastIdeasCacheKey: ReadonlySignal<string>;
    readonly slowIdeaCacheKey: ReadonlySignal<string>;
    readonly scrolling: ReadonlySignal<boolean>;
    readonly stickyFilterValue: ReadonlySignal<string>;
    readonly columnOptions: ReadonlySignal<Partial<ColumnOptions>>;
    readonly clientSortModel: ReadonlySignal<ClientSortModel>;
    setClientSortModel(sortModel: ClientSortModel): void;
    updateConcreteTemplate(template: ColumnTemplate): void;
    userSelectedSymbol(symbol: string): void;
    toJSON(): ResultsWidgetState;
    markClean(): void;
}

type TemplateColumnWithSort = TemplateColumn & Pick<Required<TemplateColumn>, 'sort'>;

/**
 * Derives the initial client sort model from a resolved template's persisted
 * per-column sort state, so the model seeds the sort before the first table
 * request instead of waiting for a grid event to push it afterwards.
 *
 * Unlike the legacy `useInitialClientSortModelFromColumnTemplate`, a column
 * qualifies on `sort` alone: legacy ag-grid only persisted `sortIndex` for
 * multi-column sorts, so single-sort templates carry `sort` without
 * `sortIndex`. Legacy tolerated dropping those here because ag-grid re-emitted
 * the template-seeded sort through its own sortChanged event; the owned grid
 * deliberately never re-emits controlled sort state (AC-SORT-LAYOUT-003), so
 * this seed is the only channel — dropping them created the table unsorted
 * while the header still painted the arrow. `sortIndex` orders multi-sort
 * members (primary first, matching `applySortModelToTemplate`).
 */
export function clientSortModelFromTemplate(template: ColumnTemplate | null): ClientSortModel {
    if (!template) return [];
    return template.columns
        .filter((col): col is TemplateColumnWithSort => col.sort !== undefined)
        .toSorted((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
        .map((col) => ({ id: col.id, sort: col.sort === 'asc' ? ('ascending' as const) : ('descending' as const) }));
}

/**
 * The self-owned (ADR 0008) Results widget state model. Composes per-concern
 * `createModel` units — column template, idea selection, sticky filter,
 * column options — and re-exposes their EventBus contracts as one bus.
 * Nested models register with this model's disposal, so teardown cascades.
 */
export const ResultsSelfOwnedModel: ModelConstructor<ResultsSelfOwnedModelShape, [ResultsSelfOwnedModelInit]> =
    createModel<ResultsSelfOwnedModelShape, [ResultsSelfOwnedModelInit]>((init: ResultsSelfOwnedModelInit) => {
        const clientSortModel: Signal<ClientSortModel> = signal([]);

        const columnTemplateModel = new ColumnTemplateModel({
            persisted: { columnTemplate: init.persisted.columnTemplate },
            templateKey: 'ticker',
            queryClient: init.queryClient,
            logger: init.logger,
            onTemplateApplied: (template, cause) => {
                // A different template (initial load or library selection)
                // carries its own persisted sort; in-place edits keep the
                // active sort — sort gestures flow through setClientSortModel.
                if (cause === 'concrete-update') return;
                clientSortModel.value = clientSortModelFromTemplate(template);
            },
        });

        const ideaSelectionModel = new IdeaSelectionModel({
            persisted: { ideas: init.persisted.ideas, universeId: init.persisted.universeId },
            logger: init.logger,
            resolvers: init.ideaResolvers,
            multiIdeaSelectEnabled: init.multiIdeaSelectEnabled,
        });

        const stickyFilterModel = new StickyFilterModel({ persisted: { filter: init.persisted.filter } });
        const columnOptionsModel = new ColumnOptionsModel({
            persisted: { columnOptions: init.persisted.columnOptions },
        });

        const scrolling = signal(init.persisted.scrolling);
        const cleanScrolling = signal(init.persisted.scrolling);

        const channelSlice =
            init.mode === 'workspace'
                ? new ContainerChannelSlice({ container: init.container, tabId: init.tabId })
                : undefined;
        const widgetTabSlice =
            init.mode === 'workspace'
                ? new ContainerWidgetTabSlice({ container: init.container, tabId: init.tabId, logger: init.logger })
                : undefined;

        // A single selected fast idea names the widget; otherwise the tab
        // name (workspace) or the generic widget name (standalone).
        const widgetName: ReadonlySignal<string> = computed(() => {
            const ideas = ideaSelectionModel.fastIdeas.value;
            if (widgetTabSlice) {
                if (ideas.length === 1 && ideas[0].name) return ideas[0].name;
                return widgetTabSlice.name.value;
            }
            return ideas[0]?.name ?? t('widgets::results::name');
        });

        const publishSymbolSelection = (instrument: string): void => {
            if (init.mode !== 'workspace') return;
            void init.workspaceService
                .publishLinkIntent({
                    type: 'instrument-selected',
                    fromTabId: init.tabId,
                    details: { instrument },
                })
                .catch((error: unknown) => {
                    init.logger.error('Failed to publish Results link intent', {
                        error,
                        instrument,
                        tabId: init.tabId,
                    });
                });
        };

        // Input readiness: template resolved (or failed) + ideas resolved (or
        // failed). Table-run errors are layered on by ResultsTableModel.
        const inputState: ReadonlySignal<ResultsWidgetRuntimeState> = computed(() => {
            const templateError = columnTemplateModel.columnTemplateError.value;
            if (templateError) {
                return { type: 'error', stage: 'column-template', error: templateError };
            }
            const ideaState: IdeaSelectionRuntimeState = ideaSelectionModel.state.value;
            if (ideaState.type === 'error') {
                return { type: 'error', stage: ideaState.stage, error: ideaState.error };
            }
            if (ideaState.type === 'loading' || columnTemplateModel.columnTemplate.value === null) {
                return { type: 'loading' };
            }
            return { type: 'ready' };
        });

        const ownEventBus = EventBusBuilder.create()
            .addSignal('widget::name', () => widgetName)
            .addSignal('widget::isScrollingEnabled', () => computed(() => scrolling.value))
            .addEvent('widget::toggleScrolling', () => {
                scrolling.value = !scrolling.value;
            })
            .addSignal('widget::clientSortModel', () => computed(() => clientSortModel.value))
            .addEvent('widget::setClientSortModel', ({ sortModel }) => {
                clientSortModel.value = sortModel;
            })
            .addEvent('widget::instrumentChange', ({ instrument }) => {
                publishSymbolSelection(instrument);
            })
            .build();

        // Inert stand-ins keep the bus surface uniform in standalone mode so
        // consumers never read a missing key. Standalone headers omit the
        // channel picker and tab controls, so these are never interacted with.
        const channelBus: ContainerChannelSliceEventBus =
            channelSlice?.eventBus ??
            EventBusBuilder.create()
                .addSignal('widget::channel', () => computed<string | null>(() => null))
                .addEvent('widget::channelChange', () => undefined)
                .addEvent('widget::resetChannelData', () => undefined)
                .build();

        const widgetTabBus: ContainerWidgetTabSliceEventBus =
            widgetTabSlice?.eventBus ??
            EventBusBuilder.create()
                .addSignal('widget::tabId', () => computed<string>(() => ''))
                .addSignal('widget::type', () => computed<string>(() => 'results'))
                .addSignal('widget::name', () => widgetName)
                .addSignal('widget::isMaximized', () => computed<boolean>(() => false))
                .addSignal('widget::container', () =>
                    computed<ContainerViewModel>(() => ({ id: '', activeTabId: null, tabIds: [] })),
                )
                .addSignal('widget::containerActiveTab', () =>
                    computed<WidgetTabViewModel | undefined>(() => undefined),
                )
                .addSignal('widget::tabsById', () => computed<ByIdState>(() => ({})))
                .addEvent('widget::toggleMaximized', () => undefined)
                .addEvent('widget::delete', () => undefined)
                .addEvent('widget::changeTab', () => undefined)
                .addEvent('widget::deleteTab', () => undefined)
                .addEvent('widget::changeTabName', () => undefined)
                .build();

        const eventBus: ResultsSelfOwnedEventBus = MergedEventBus.from<ContainerChannelSliceEventBus>(channelBus)
            .merge<ContainerWidgetTabSliceEventBus>(widgetTabBus)
            .merge<StickyFilterModelEventBus>(stickyFilterModel.eventBus)
            .merge<ColumnOptionsModelEventBus>(columnOptionsModel.eventBus)
            .merge<ColumnTemplateModelEventBus>(columnTemplateModel.eventBus)
            .merge<IdeaSelectionModelEventBus>(ideaSelectionModel.eventBus)
            // Own bus last: its widget::name (idea-derived) overrides the tab slice's.
            .merge(ownEventBus);

        return {
            dirty: computed(
                () =>
                    columnTemplateModel.dirty.value ||
                    ideaSelectionModel.dirty.value ||
                    stickyFilterModel.dirty.value ||
                    columnOptionsModel.dirty.value ||
                    scrolling.value !== cleanScrolling.value,
            ),
            eventBus,
            inputState,
            columnTemplate: columnTemplateModel.columnTemplate,
            columnTemplateRef: columnTemplateModel.columnTemplateRef,
            fastIdeas: ideaSelectionModel.fastIdeas,
            slowIdea: ideaSelectionModel.slowIdea,
            fastIdeasCacheKey: ideaSelectionModel.fastIdeasCacheKey,
            slowIdeaCacheKey: ideaSelectionModel.slowIdeaCacheKey,
            scrolling: computed(() => scrolling.value),
            stickyFilterValue: stickyFilterModel.eventBus.getSignal('widget::stickyFilterValue'),
            columnOptions: columnOptionsModel.columnOptions,
            clientSortModel: computed(() => clientSortModel.value),
            setClientSortModel(sortModel: ClientSortModel): void {
                clientSortModel.value = sortModel;
            },
            updateConcreteTemplate(template: ColumnTemplate): void {
                columnTemplateModel.updateConcreteTemplate(template);
            },
            userSelectedSymbol(symbol: string): void {
                publishSymbolSelection(symbol);
            },
            toJSON(): ResultsWidgetState {
                return {
                    ...ideaSelectionModel.toJSON(),
                    ...columnTemplateModel.toJSON(),
                    ...stickyFilterModel.toJSON(),
                    ...columnOptionsModel.toJSON(),
                    scrolling: scrolling.value,
                };
            },
            markClean(): void {
                columnTemplateModel.markClean();
                ideaSelectionModel.markClean();
                stickyFilterModel.markClean();
                columnOptionsModel.markClean();
                cleanScrolling.value = scrolling.value;
            },
        };
    });

export type ResultsSelfOwnedModel = Model<ResultsSelfOwnedModelShape>;
